/**
 * Micros and currency units, converted exactly and in both directions.
 *
 * The platform's unit of account is a MILLIONTH of a minor unit — a token
 * price is routinely a fraction of a cent — so a routing profile's spend
 * ceiling is stored as an integer number of micros. That integer is the right
 * thing to send and the wrong thing to type: a five dollar per-request cap is
 * `500000000`, and an operator who drops a zero has written fifty cents and
 * will not see it.
 *
 * So the boxes take currency units and this module does the conversion. Every
 * step is BigInt and string arithmetic, never a float: `5.55 * 1e8` is
 * 555000000.0000001 in IEEE 754, and a ceiling that is one micro off the one
 * an operator typed is exactly the kind of discrepancy nobody can explain six
 * months later.
 *
 * The scale is `internal/money`'s: micros per MAJOR unit is
 * MicrosPerMinor x 10^exponent, so 100,000,000 for USD and 1,000,000 for JPY.
 * Getting the exponent wrong is a 100x error, which is why the caller passes
 * one in rather than this module assuming two — see profileCurrency in
 * routing-profile-form.tsx for where a profile's comes from.
 */

/** Mirrors internal/money.MicrosPerMinor: micros per MINOR unit (a cent). */
const MICROS_PER_MINOR = 1_000_000n;

/**
 * The minor-unit exponents internal/money holds, mirrored for the same reason
 * models.tsx mirrors the provider registry: to decide what to draw. A code
 * this map does not know returns undefined rather than 2 — internal/money
 * refuses an unknown code at construction rather than assuming two decimal
 * places, and a console that assumed instead would be the one place in the
 * platform that guessed.
 */
const CURRENCY_EXPONENTS: Record<string, number | undefined> = {
  USD: 2,
  AUD: 2,
  EUR: 2,
  GBP: 2,
  NZD: 2,
  CAD: 2,
  SGD: 2,
  JPY: 0,
};

/**
 * The scale used when nothing on the page has named a currency.
 *
 * Seven of the eight currencies the platform transacts in have it, so it is
 * the least wrong assumption available — but it IS an assumption, and every
 * screen that falls back to it says "units" rather than a currency code and
 * prints the exact micros the request will carry beside the box.
 */
export const ASSUMED_EXPONENT = 2;

export function currencyExponent(currency: string | undefined): number | undefined {
  return currency === undefined ? undefined : CURRENCY_EXPONENTS[currency.toUpperCase()];
}

/** Micros per major unit at this exponent. */
export function microsPerUnit(exponent: number): bigint {
  let per = MICROS_PER_MINOR;
  for (let i = 0; i < exponent; i += 1) {
    per *= 10n;
  }
  return per;
}

/**
 * The most fractional digits a unit figure can carry without losing a micro:
 * the currency's own decimal places plus the six that make a micro.
 */
export function maxFractionDigits(exponent: number): number {
  return exponent + 6;
}

function withThousands(whole: string): string {
  return whole.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

/**
 * Micros as a unit figure — 500000000 at exponent 2 is "5.00".
 *
 * Trailing zeros are trimmed down to the currency's own decimal places and no
 * further, so a whole-cent ceiling reads as "5.00" and one that is not a whole
 * cent is VISIBLY not: "0.000015" stays six digits long rather than rounding
 * to "0.00", which would show a real ceiling as no ceiling at all.
 *
 * `group` puts separators in for display. The form boxes ask for them off:
 * a value the operator cannot re-submit unchanged is a box that fights back.
 */
export function formatUnits(micros: number, exponent: number, group = true): string {
  // A JSON number is a float64. Every micros figure the admin API sends fits
  // in 2^53 by a wide margin, but a value that did not could not be converted
  // exactly at all, and printing it raw is the only honest answer left.
  if (!Number.isSafeInteger(micros)) {
    return String(micros);
  }
  const negative = micros < 0;
  const magnitude = negative ? -BigInt(micros) : BigInt(micros);
  const per = microsPerUnit(exponent);
  const whole = (magnitude / per).toString();
  const digits = maxFractionDigits(exponent);
  let fraction = (magnitude % per).toString().padStart(digits, "0").replace(/0+$/, "");
  while (fraction.length < exponent) {
    fraction += "0";
  }
  return `${negative ? "-" : ""}${group ? withThousands(whole) : whole}${
    fraction === "" ? "" : `.${fraction}`
  }`;
}

/** What a unit box parsed to, or the sentence to show under it. */
export type UnitsResult = { micros: bigint } | { error: string };

export function isUnitsError(result: UnitsResult): result is { error: string } {
  return "error" in result;
}

/**
 * A unit figure as integer micros.
 *
 * The rules are stated here so the operator hears them before the request
 * rather than after it; the server applies its own (0006's `>= 0` CHECK, and
 * a decoder that refuses a JSON float into an int64), and the server is the
 * control.
 *
 * A separator is REFUSED rather than stripped. "1,5" is one and a half in half
 * of Europe and fifteen with a typo everywhere else, and a limit is not a field
 * to guess on.
 */
export function parseUnits(text: string, exponent: number): UnitsResult {
  const trimmed = text.trim();
  if (trimmed.startsWith("-")) {
    return {
      error: "A spend limit cannot be negative. Leave it empty when this profile sets none.",
    };
  }
  const match = /^(\d+)(?:\.(\d+))?$/.exec(trimmed);
  if (!match) {
    return { error: "Digits and a decimal point only — no currency symbol and no separators." };
  }
  const digits = maxFractionDigits(exponent);
  const whole = match[1] ?? "0";
  const fraction = match[2] ?? "";
  if (fraction.length > digits) {
    return {
      error: `At most ${digits} decimal places — the platform stores millionths of a ${
        exponent === 0 ? "unit" : "cent"
      } and cannot hold a smaller one.`,
    };
  }
  const micros = BigInt(whole) * microsPerUnit(exponent) + BigInt(fraction.padEnd(digits, "0"));
  // The wire is JSON, so the figure has to survive a float64 on both sides of
  // it. This bound is ~90 million dollars at exponent 2, far above any real
  // ceiling and far below the int64 the column holds.
  if (micros > BigInt(Number.MAX_SAFE_INTEGER)) {
    return { error: "That is larger than a spend limit this platform can hold." };
  }
  return { micros };
}
